import { useState } from "react";
import { login, stepUp, AuthError, type ConsoleUser } from "../lib/auth";
import { Button, Card, Field, ErrorBanner } from "../components";
import { theme, fontStack, radius } from "../theme";

/**
 * ADMIN-12 — écran de connexion en deux temps : facteur 1 (mot de passe) puis
 * step-up 2FA (code TOTP ou code de secours). Le facteur 1 seul n'ouvre rien —
 * il ne rend qu'un pré-token qui est aussitôt échangé au step-up.
 */
export function Login({
  onAuthed,
  onNeedsEnroll,
}: {
  onAuthed: (u: ConsoleUser) => void;
  onNeedsEnroll?: (email: string, password: string) => void;
}) {
  const [phase, setPhase] = useState<"password" | "totp">("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [stepUpToken, setStepUpToken] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { stepUpToken } = await login(email.trim(), password);
      setStepUpToken(stepUpToken);
      setPhase("totp");
    } catch (err) {
      // Admin fraîchement promu, TOTP pas encore activé : on bascule vers
      // l'enrôlement en portant les identifiants déjà saisis (facteur 1 valide).
      if (err instanceof AuthError && err.code === "CONSOLE_TOTP_REQUIRED" && onNeedsEnroll) {
        onNeedsEnroll(email.trim(), password);
        return;
      }
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const user = await stepUp(stepUpToken, code.trim());
      onAuthed(user);
    } catch (err) {
      setError(messageFor(err));
      // Un pré-token est à usage unique : après un échec, on repart du mot de passe.
      if (err instanceof AuthError && err.code === "STEPUP_INVALID") setPhase("password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <Card style={{ width: 380, maxWidth: "100%" }}>
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontFamily: fontStack.mono, color: theme.accent, fontSize: 13, letterSpacing: 1 }}>
            LIVEMESSAGE
          </div>
          <h1 style={{ fontFamily: fontStack.uiSemiBold, fontSize: 22, marginTop: 4 }}>
            Console admin
          </h1>
          <p style={{ color: theme.ink3, fontSize: 13, marginTop: 6 }}>
            {phase === "password" ? "Connexion sécurisée · facteur 1" : "Double authentification · facteur 2"}
          </p>
        </div>

        {error && <ErrorBanner message={error} />}

        {phase === "password" ? (
          <form onSubmit={submitPassword}>
            <Field
              label="E-mail"
              type="email"
              value={email}
              autoComplete="username"
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Field
              label="Mot de passe"
              type="password"
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <Button type="submit" disabled={busy} style={{ width: "100%", marginTop: 6 }}>
              {busy ? "…" : "Continuer"}
            </Button>
          </form>
        ) : (
          <form onSubmit={submitCode}>
            <Field
              label="Code d'authentification (TOTP ou code de secours)"
              type="text"
              inputMode="text"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              required
            />
            <Button type="submit" disabled={busy} style={{ width: "100%", marginTop: 6 }}>
              {busy ? "…" : "Ouvrir la console"}
            </Button>
            <button
              type="button"
              onClick={() => {
                setPhase("password");
                setCode("");
                setError(null);
              }}
              style={{
                width: "100%",
                marginTop: 12,
                background: "transparent",
                border: "none",
                color: theme.ink3,
                fontSize: 13,
                borderRadius: radius.pill,
              }}
            >
              ← Recommencer
            </button>
          </form>
        )}
      </Card>
    </div>
  );
}

function messageFor(err: unknown): string {
  if (err instanceof AuthError) {
    switch (err.code) {
      case "INVALID_CREDENTIALS":
        return "E-mail ou mot de passe incorrect.";
      case "ADMIN_REQUIRED":
        return "Ce compte n'a pas accès à la console.";
      case "CONSOLE_TOTP_REQUIRED":
        return "La double authentification n'est pas encore activée sur ce compte.";
      case "TOTP_LOCKED":
        return "Trop de tentatives : compte temporairement verrouillé. Réessayez plus tard.";
      case "TOTP_INVALID":
        return "Code incorrect.";
      case "STEPUP_INVALID":
        return "Session de connexion expirée, veuillez recommencer.";
      default:
        return err.message;
    }
  }
  return "Une erreur est survenue.";
}
